import { motion } from "framer-motion";
import { CalendarClock, Sparkles, Zap, Phone } from "lucide-react";
import { BUSINESS, IMAGES } from "../lib/constants";

const STEPS = [
  {
    n: "01",
    icon: CalendarClock,
    title: "Set your time",
    body: "Pick your service, count your panes or windows, and lock a date and arrival window — your exact total is calculated before you commit.",
  },
  {
    n: "02",
    icon: Sparkles,
    title: "We arrive & detail",
    body: "On time, every time. Inside and outside glass, squeegee-detailed pane by pane, edges hand-finished — until the glass disappears.",
  },
  {
    n: "03",
    icon: Zap,
    title: "Pay with Zelle",
    body: "No invoices, no waiting. Send your total straight from your banking app to our Zelle and you're done before we roll up the hose.",
  },
];

export default function HowItWorks({ onBook }) {
  return (
    <section id="process" className="relative py-24 sm:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-16"
        >
          <span className="block font-display italic text-7xl sm:text-8xl leading-none text-outline select-none mb-4" aria-hidden="true">03</span>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-9 h-[1px] bg-crystal" />
            <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-crystal">The Process</span>
          </div>
          <h2 className="font-display font-medium text-[clamp(2.2rem,5vw,4.2rem)] leading-[1.02] text-white tracking-tight">
            Three moves to <em className="italic text-crystal">invisible glass.</em>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group relative rounded-3xl glass-panel p-8 sm:p-10 h-full transition-transform duration-200 hover:-translate-y-1"
              data-testid={`process-step-${s.n}`}
            >
              <span className="absolute top-7 right-8 font-display italic text-6xl text-white/[0.07] group-hover:text-crystal/20 transition-colors duration-300">
                {s.n}
              </span>
              <s.icon size={22} strokeWidth={1.6} className="text-crystal" />
              <h3 className="mt-6 font-display text-3xl text-white">{s.title}</h3>
              <p className="mt-4 text-mist text-sm leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mt-20 relative overflow-hidden rounded-3xl border border-crystal/25"
          style={{ boxShadow: "0 0 120px -50px rgba(0,229,255,0.5)" }}
        >
          <img src={IMAGES.squeegee} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-midnight/80" />
          <div className="relative z-10 flex flex-col items-start gap-6 p-10 sm:p-16">
            <h3 className="font-display font-medium text-[clamp(1.8rem,4vw,3.2rem)] leading-tight text-white max-w-2xl">
              Ready to see through your windows again?
            </h3>
            <div className="flex flex-wrap items-center gap-4">
              <button
                data-testid="cta-book-btn"
                onClick={() => onBook({})}
                className="rounded-full bg-crystal px-8 py-4 font-mono text-[11px] font-semibold tracking-[0.25em] uppercase text-midnight transition-transform duration-200 hover:-translate-y-0.5"
              >
                Book a time
              </button>
              <a
                href={BUSINESS.phoneHref}
                data-testid="cta-phone-link"
                className="flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase text-white hover:border-crystal hover:text-crystal transition-colors duration-200"
              >
                <Phone size={14} /> {BUSINESS.phone}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
